/**
 * A standard 52-card deck (plus up to two jokers), shuffled and dealt without
 * replacement, with the probabilities that go with it.
 *
 * Cards are plain values, not objects with identity, so a shuffled deck is just an array.
 * The shuffle is Fisher–Yates over `crypto.getRandomValues` (see `rng.ts`), which makes
 * all 52! orderings equally likely — roughly 8 × 10⁶⁷ of them.
 */

import { shuffle, type Uint32Source } from "@/lib/tool-kit/random-fun/rng";

export type Suit = "S" | "H" | "D" | "C";
/** 1 is the ace, 11 jack, 12 queen, 13 king. */
export type Rank = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13;

export type Card =
  | { kind: "suit"; rank: Rank; suit: Suit }
  | { kind: "joker"; index: number };

export const SUITS: readonly Suit[] = ["S", "H", "D", "C"];
export const RANKS: readonly Rank[] = [
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13,
];
export const SUIT_SYMBOL: Record<Suit, string> = {
  S: "♠",
  H: "♥",
  D: "♦",
  C: "♣",
};
export const MAX_JOKERS = 2;
export const DECK_SIZE = 52;

export function isRed(card: Card): boolean {
  return card.kind === "suit" && (card.suit === "H" || card.suit === "D");
}

/** "A", "2" … "10", "J", "Q", "K". */
export function rankSymbol(rank: Rank): string {
  switch (rank) {
    case 1:
      return "A";
    case 11:
      return "J";
    case 12:
      return "Q";
    case 13:
      return "K";
    default:
      return String(rank);
  }
}

/** A fresh deck in factory order: spades, hearts, diamonds, clubs, ace to king. */
export function newDeck(jokers: number): Card[] {
  const deck: Card[] = [];
  for (const suit of SUITS) {
    for (const rank of RANKS) {
      deck.push({ kind: "suit", rank, suit });
    }
  }
  for (
    let index = 0;
    index < Math.min(MAX_JOKERS, Math.max(0, jokers));
    index += 1
  ) {
    deck.push({ kind: "joker", index });
  }
  return deck;
}

export function shuffledDeck(jokers: number, source?: Uint32Source): Card[] {
  return shuffle(newDeck(jokers), source);
}

/** Takes `count` cards off the top; the rest stay in the deck. */
export function deal(
  deck: readonly Card[],
  count: number
): { hand: Card[]; rest: Card[] } {
  const take = Math.min(Math.max(0, Math.trunc(count)), deck.length);
  return { hand: deck.slice(0, take), rest: deck.slice(take) };
}

/** Short text such as `A♠` or `10♥`; the caller supplies the word for a joker. */
export function cardText(card: Card, jokerWord: string): string {
  return card.kind === "joker"
    ? jokerWord
    : `${rankSymbol(card.rank)}${SUIT_SYMBOL[card.suit]}`;
}

/* ------------------------------------------------------------- probabilities */

/**
 * The chance that at least one of `draws` cards, dealt without replacement from `total`
 * cards of which `successes` count, is a success: 1 − C(total − successes, draws) / C(total, draws).
 *
 * Computed as the product of the chances of missing on each successive card, which keeps
 * every factor between 0 and 1 and avoids huge binomial coefficients.
 */
export function chanceOfAtLeastOne(
  total: number,
  successes: number,
  draws: number
): number {
  if (successes <= 0 || draws <= 0) {
    return 0;
  }
  if (successes >= total || draws > total - successes) {
    return 1;
  }
  let missAll = 1;
  for (let i = 0; i < draws; i += 1) {
    missAll *= (total - successes - i) / (total - i);
  }
  return 1 - missAll;
}

export type TargetId = "specific" | "ace" | "suit" | "face" | "red" | "joker";

export interface Target {
  id: TargetId;
  /** How many cards in the deck match. */
  count: number;
}

/** The questions the odds table answers, with how many cards of the deck match each. */
export function targets(jokers: number): Target[] {
  const list: Target[] = [
    { id: "specific", count: 1 },
    { id: "ace", count: 4 },
    { id: "suit", count: 13 },
    { id: "face", count: 12 },
    { id: "red", count: 26 },
  ];
  if (jokers > 0) {
    list.push({ id: "joker", count: Math.min(MAX_JOKERS, jokers) });
  }
  return list;
}

/* --------------------------------------------------------------- poker hands */

export type PokerHand =
  | "royalFlush"
  | "straightFlush"
  | "fourOfAKind"
  | "fullHouse"
  | "flush"
  | "straight"
  | "threeOfAKind"
  | "twoPair"
  | "onePair"
  | "highCard";

/**
 * How many of the 2,598,960 five-card hands (C(52,5)) make each poker hand, counting each
 * hand once as its best category. The classifier below is checked against a count of
 * every one of them in the test suite.
 */
export const POKER_HAND_COUNTS: Record<PokerHand, number> = {
  royalFlush: 4,
  straightFlush: 36,
  fourOfAKind: 624,
  fullHouse: 3744,
  flush: 5108,
  straight: 10_200,
  threeOfAKind: 54_912,
  twoPair: 123_552,
  onePair: 1_098_240,
  highCard: 1_302_540,
};

export const FIVE_CARD_HANDS = 2_598_960;

export const POKER_HANDS_BEST_FIRST: readonly PokerHand[] = [
  "royalFlush",
  "straightFlush",
  "fourOfAKind",
  "fullHouse",
  "flush",
  "straight",
  "threeOfAKind",
  "twoPair",
  "onePair",
  "highCard",
];

const HAND_SIZE = 5;
const ROYAL_RANKS = [1, 10, 11, 12, 13];

function isStraight(sortedRanks: readonly number[]): boolean {
  const first = sortedRanks[0] ?? 0;
  if (sortedRanks.every((rank, i) => rank === first + i)) {
    return true;
  }
  // The ace also plays high: 10-J-Q-K-A.
  return sortedRanks.every((rank, i) => rank === ROYAL_RANKS[i]);
}

/** The categories decided only by how many cards share a rank. */
function byMultiples(groups: readonly number[]): PokerHand {
  if (groups[0] === 3) {
    return "threeOfAKind";
  }
  if (groups[0] === 2 && groups[1] === 2) {
    return "twoPair";
  }
  return groups[0] === 2 ? "onePair" : "highCard";
}

/** The best poker category of exactly five suited cards, or null for any other hand. */
export function classifyPokerHand(cards: readonly Card[]): PokerHand | null {
  if (cards.length !== HAND_SIZE) {
    return null;
  }
  const ranks: number[] = [];
  const suits = new Set<Suit>();
  for (const card of cards) {
    if (card.kind === "joker") {
      return null;
    }
    ranks.push(card.rank);
    suits.add(card.suit);
  }
  ranks.sort((a, b) => a - b);
  const counts = new Map<number, number>();
  for (const rank of ranks) {
    counts.set(rank, (counts.get(rank) ?? 0) + 1);
  }
  const groups = [...counts.values()].sort((a, b) => b - a);
  const flush = suits.size === 1;
  const straight = groups[0] === 1 && isStraight(ranks);
  if (straight && flush) {
    return ranks[0] === 1 && ranks[1] === 10 ? "royalFlush" : "straightFlush";
  }
  if (groups[0] === 4) {
    return "fourOfAKind";
  }
  if (groups[0] === 3 && groups[1] === 2) {
    return "fullHouse";
  }
  if (flush) {
    return "flush";
  }
  if (straight) {
    return "straight";
  }
  return byMultiples(groups);
}
